import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Modal from '../../components/ui/Modal';
import { careersService } from '../../services/careersService';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null); // For details modal
  const [showApplyModal, setShowApplyModal] = useState(false); // For application form
  const [applicationForm, setApplicationForm] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    experience_summary: '',
    additional_info: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const [data, visibility] = await Promise.all([
        careersService.getActiveJobs(),
        careersService.getGlobalVisibility()
      ]);
      
      if (!visibility) {
        setJobs([]);
      } else {
        setJobs(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeptClick = (deptName) => {
    setFilterDept(deptName);
    document.getElementById('positions').scrollIntoView({ behavior: 'smooth' });
  };

  // Filter by closing date (if present) and department
  const now = new Date();
  const activeJobs = jobs.filter(j => !j.closing_at || new Date(j.closing_at) > now);

  const filteredJobs = filterDept === 'All' 
    ? activeJobs 
    : activeJobs.filter(j => j.department === filterDept);

  const handleApplyClick = () => {
    setShowApplyModal(true);
  };

  const handleGeneralApply = () => {
    setSelectedJob({ id: null, title: 'General Application', isGeneral: true });
    setShowApplyModal(true);
  };

  const handleCloseApplyModal = () => {
    setShowApplyModal(false);
    if (selectedJob?.isGeneral) {
        setSelectedJob(null);
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;
    
    // Validate phone number
    if (!applicationForm.phone || applicationForm.phone.trim() === '') {
        alert('Phone number is required.');
        return;
    }
    
    setSubmitting(true);
    try {
      await careersService.submitApplication({
        job_id: selectedJob.id || null, // Allow null for general application
        ...applicationForm
      });
      // Close modals
      setShowApplyModal(false);
      setSelectedJob(null);
      // Reset form
      setApplicationForm({ applicant_name: '', email: '', phone: '', experience_summary: '', additional_info: '' });
      alert('Application submitted successfully! We will be in touch.');
    } catch (err) {
      console.error(err);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const departments = [
    { name: 'Engineering', count: jobs.filter(j => j.department === 'Engineering').length, icon: 'Code' },
    { name: 'Design', count: jobs.filter(j => j.department === 'Design').length, icon: 'Palette' },
    { name: 'Product', count: jobs.filter(j => j.department === 'Product').length, icon: 'Box' },
    { name: 'Marketing', count: jobs.filter(j => j.department === 'Marketing').length, icon: 'TrendingUp' },
    { name: 'Sales', count: jobs.filter(j => j.department === 'Sales').length, icon: 'DollarSign' },
    { name: 'Support', count: jobs.filter(j => j.department === 'Support').length, icon: 'LifeBuoy' }
  ];

  const benefits = [
    { title: 'Remote-First', description: 'Work from anywhere in the world. We believe in results, not desks.', icon: 'Globe' },
    { title: 'Learning Budget', description: '$2,000 annual budget for books, courses, and conferences.', icon: 'BookOpen' },
    { title: 'Latest Gear', description: 'Get the best hardware and software to do your best work.', icon: 'Monitor' },
    { title: 'Flexible Hours', description: 'Manage your own schedule and find your flow state.', icon: 'Clock' },
    { title: 'Health & Wellness', description: 'Comprehensive health, dental, and vision coverage.', icon: 'Heart' },
    { title: 'Equity Options', description: 'We want everyone to have a stake in our success.', icon: 'PieChart' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary via-primary/90 to-accent text-white">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Shape the Future of 3D</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Join a team of visionaries, engineers, and designers building the infrastructure for the next generation of commerce.
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg" variant="default" className="bg-white text-primary hover:bg-white/90" onClick={() => document.getElementById('positions').scrollIntoView({ behavior: 'smooth' })}>
              View Open Positions
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => document.getElementById('culture').scrollIntoView({ behavior: 'smooth' })}>
              Our Culture
            </Button>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section id="culture" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Our Culture & Values</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                At OpenGadget, we're more than just a company. We're a distributed team of enthusiasts passionate about breaking barriers in 3D visualization. We value autonomy, transparency, and continuous growth.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="mt-1 text-primary"><Icon name="CheckCircle" size={20} /></div>
                  <p className="text-slate-700"><strong>Ownership:</strong> We give you the space to lead and make a real impact.</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="mt-1 text-primary"><Icon name="CheckCircle" size={20} /></div>
                  <p className="text-slate-700"><strong>Inclusion:</strong> We believe diversity of thought drives innovation.</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="mt-1 text-primary"><Icon name="CheckCircle" size={20} /></div>
                  <p className="text-slate-700"><strong>Agility:</strong> We move fast, learn faster, and adapt always.</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
               <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  <img src="/assets/images/team-collab.jpg" alt="Team collaboration" className="w-full h-full object-cover" />
               </div>
               <div className="absolute -bottom-6 -left-6 bg-accent text-white p-6 rounded-xl shadow-lg hidden md:block">
                  <p className="text-2xl font-bold italic">"Work should be an adventure."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Perks & Benefits</h2>
          <p className="text-slate-600">Everything you need to do your best work and live your best life.</p>
        </div>
        <div className="container mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="bg-white p-8 rounded-2xl shadow-soft hover:shadow-floating transition-smooth border border-slate-100">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-6 text-accent">
                <Icon name={benefit.icon} size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">{benefit.title}</h3>
              <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions */}
      <section id="positions" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Departments Grid moved here */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              {departments.map((dept) => (
                <div 
                  key={dept.name} 
                  className={`p-6 rounded-xl shadow-soft text-center cursor-pointer transition-smooth border-2 ${filterDept === dept.name ? 'border-primary bg-primary/5 shadow-floating' : 'bg-white border-slate-100 hover:shadow-floating hover:border-primary/20'}`}
                  onClick={() => handleDeptClick(dept.name)}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                    <Icon name={dept.icon} size={24} />
                  </div>
                  <h3 className="font-semibold text-slate-800">{dept.name}</h3>
                  <p className="text-sm text-slate-500">{dept.count} open roles</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
              <h2 className="text-3xl font-bold text-slate-900">
                {filterDept === 'All' ? 'Current Openings' : `${filterDept} Openings`}
              </h2>
              {filterDept !== 'All' && (
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setFilterDept('All')}
                    className="w-fit"
                >
                    Show All Positions
                </Button>
              )}
            </div>
            
            {loading ? (
                <p>Loading positions...</p>
            ) : filteredJobs.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <p className="text-slate-500">
                        {filterDept === 'All' 
                            ? 'No open positions at the moment. Please check back later!' 
                            : `No open positions in ${filterDept} at the moment.`}
                    </p>
                    {filterDept !== 'All' && (
                        <Button variant="ghost" className="mt-4 text-primary" onClick={() => setFilterDept('All')}>View other departments</Button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                {filteredJobs.map((pos) => (
                    <div 
                        key={pos.id} 
                        className="group p-6 border border-slate-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-smooth cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                        onClick={() => setSelectedJob(pos)}
                    >
                    <div>
                        <h3 className="text-xl font-bold text-slate-800 group-hover:text-primary transition-colors">{pos.title}</h3>
                        <div className="flex flex-wrap gap-4 mt-2">
                        <span className="flex items-center gap-1.5 text-sm text-slate-500"><Icon name="Briefcase" size={14} /> {pos.department}</span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-500"><Icon name="MapPin" size={14} /> {pos.location}</span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-500"><Icon name="Clock" size={14} /> {pos.type}</span>
                        {pos.closing_at && <span className="flex items-center gap-1.5 text-sm text-red-500"><Icon name="Calendar" size={14} /> Closes: {new Date(pos.closing_at).toLocaleDateString()}</span>}
                        </div>
                    </div>
                    <Button variant="outline" className="group-hover:bg-primary group-hover:text-white transition-colors">View Details</Button>
                    </div>
                ))}
                </div>
            )}

            <div className="mt-12 p-8 bg-slate-900 text-white rounded-2xl text-center">
              <h3 className="text-xl font-bold mb-3">Don't see the right role?</h3>
              <p className="text-slate-400 mb-6 text-sm">We're always looking for exceptional talent. Send your resume to talent@opengadget.com</p>
              <Button variant="default" onClick={handleGeneralApply}>General Application</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      {selectedJob && !showApplyModal && (
        <Modal 
            isOpen={!!selectedJob} 
            onClose={() => setSelectedJob(null)} 
            title={selectedJob.title}
            className="max-w-5xl h-[85vh]"
        >
            <div className="space-y-8 p-2">
                <div className="flex flex-wrap gap-4 border-b border-slate-100 pb-6">
                    <span className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-base font-medium">{selectedJob.department}</span>
                    <span className="bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-base font-medium">{selectedJob.location}</span>
                    <span className="bg-green-50 text-green-700 px-4 py-2 rounded-full text-base font-medium">{selectedJob.type}</span>
                    {selectedJob.salary_range && (
                        <span className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-base font-medium">{selectedJob.salary_range}</span>
                    )}
                </div>

                <div className="space-y-8">
                    <div>
                        <h4 className="text-xl font-bold text-slate-900 mb-4">About the Role</h4>
                        <p className="text-lg text-slate-600 leading-relaxed whitespace-pre-line">{selectedJob.description}</p>
                    </div>

                    {selectedJob.qualifications && Array.isArray(selectedJob.qualifications) && selectedJob.qualifications.length > 0 && (
                        <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-3">Qualifications</h4>
                            <ul className="list-disc pl-5 space-y-2 text-slate-600">
                                {selectedJob.qualifications.map((q, i) => (
                                    <li key={i}>{q}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {selectedJob.requirements && Array.isArray(selectedJob.requirements) && selectedJob.requirements.length > 0 && (
                        <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-3">Requirements</h4>
                            <ul className="list-disc pl-5 space-y-2 text-slate-600">
                                {selectedJob.requirements.map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="pt-8 mt-4 border-t border-slate-100 flex justify-end gap-4 shrink-0">
                    <Button variant="ghost" size="lg" onClick={() => setSelectedJob(null)}>Close</Button>
                    <Button variant="default" size="lg" onClick={handleApplyClick}>Apply Now</Button>
                </div>
            </div>
        </Modal>
      )}

      {/* Application Modal */}
      {showApplyModal && selectedJob && (
        <Modal 
            isOpen={showApplyModal} 
            onClose={handleCloseApplyModal} 
            title={selectedJob.isGeneral ? "Submit General Application" : `Apply for ${selectedJob.title}`}
            className="max-w-4xl h-[85vh]"
        >
            <form onSubmit={handleApplySubmit} className="space-y-6 p-2">
                <div className="grid md:grid-cols-2 gap-6">
                    <Input 
                        label="Full Name" 
                        required 
                        placeholder="e.g. John Doe"
                        value={applicationForm.applicant_name}
                        onChange={(e) => setApplicationForm({...applicationForm, applicant_name: e.target.value})}
                    />
                    <Input 
                        label="Email Address" 
                        type="email" 
                        required 
                        placeholder="e.g. john@example.com"
                        value={applicationForm.email}
                        onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                    />
                </div>
                <Input 
                    label="Phone Number" 
                    type="tel" 
                    required
                    placeholder="e.g. +1 (555) 000-0000"
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                />
                <Textarea 
                    label="Experience Summary" 
                    required 
                    placeholder="Briefly describe your relevant experience, key projects, and achievements..."
                    rows={6}
                    value={applicationForm.experience_summary}
                    onChange={(e) => setApplicationForm({...applicationForm, experience_summary: e.target.value})}
                />
                <Textarea 
                    label="Additional Information" 
                    placeholder="Links to portfolio, LinkedIn, personal website, or anything else you'd like to share..."
                    rows={4}
                    value={applicationForm.additional_info}
                    onChange={(e) => setApplicationForm({...applicationForm, additional_info: e.target.value})}
                />
                
                <div className="pt-8 border-t border-slate-100 flex justify-end gap-4 shrink-0">
                    <Button variant="ghost" size="lg" type="button" onClick={handleCloseApplyModal}>Cancel</Button>
                    <Button variant="default" size="lg" type="submit" disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                </div>
            </form>
        </Modal>
      )}

      <Footer />
    </div>
  );
};

export default Careers;